export const TRACKING_PARAMS = [
  'adgroupid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'hsa_acc',
  'hsa_cam',
  'hsa_grp',
  'hsa_ad',
  'hsa_src',
  'hsa_tgt',
  'hsa_kw',
  'hsa_mt',
  'hsa_net',
  'hsa_ver',
] as const;

/** Optional campaign context, e.g. the festive gifting forms. */
export const CALLBACK_CONTEXT_FIELDS = ['source', 'planId', 'giftFor'] as const;

export type PerformanceLabCallbackPayload = {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  source?: string;
  planId?: string;
  giftFor?: string;
  adgroupid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  hsa_acc?: string;
  hsa_cam?: string;
  hsa_grp?: string;
  hsa_ad?: string;
  hsa_src?: string;
  hsa_tgt?: string;
  hsa_kw?: string;
  hsa_mt?: string;
  hsa_net?: string;
  hsa_ver?: string;
};

export type PerformanceLabCallbackSubmitBody = PerformanceLabCallbackPayload & {
  /** Honeypot — must stay empty; bots often fill hidden fields. */
  _hp?: string;
};

export type PerformanceLabCallbackApiSuccess = {
  status: 'ok';
  data: { id: number | string };
};

export type PerformanceLabCallbackApiError = {
  status: 'error';
  error?: { message?: string };
  message?: string;
};

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const normalizePerformanceLabCallbackPayload = (
  body: unknown,
): { payload?: PerformanceLabCallbackPayload; error?: string } => {
  if (!body || typeof body !== 'object') {
    return { error: 'Invalid request body.' };
  }

  const raw = body as Record<string, unknown>;

  if (typeof raw._hp === 'string' && raw._hp.trim().length > 0) {
    return { error: 'Invalid submission.' };
  }

  const firstName =
    typeof raw.firstName === 'string' ? raw.firstName.trim() : '';
  const lastName = typeof raw.lastName === 'string' ? raw.lastName.trim() : '';
  const phone = typeof raw.phone === 'string' ? raw.phone.trim() : '';
  const emailRaw = typeof raw.email === 'string' ? raw.email.trim() : '';

  if (!firstName || firstName.length > 100) {
    return { error: 'First name is required.' };
  }
  if (!lastName || lastName.length > 100) {
    return { error: 'Last name is required.' };
  }
  if (!phone || phone.length > 30) {
    return { error: 'Phone number is required.' };
  }
  if (emailRaw && (emailRaw.length > 254 || !EMAIL_RE.test(emailRaw))) {
    return { error: 'Enter a valid email address.' };
  }

  const payload: PerformanceLabCallbackPayload = {
    firstName,
    lastName,
    phone,
  };

  if (emailRaw) {
    payload.email = emailRaw;
  }

  TRACKING_PARAMS.forEach((param) => {
    const val = raw[param];
    if (typeof val === 'string' && val.trim().length > 0) {
      payload[param] = val.trim();
    }
  });

  CALLBACK_CONTEXT_FIELDS.forEach((field) => {
    const val = raw[field];
    if (typeof val === 'string' && val.trim().length > 0) {
      payload[field] = val.trim().slice(0, 64);
    }
  });

  return { payload };
};

export const submitPerformanceLabCallback = async (
  body: PerformanceLabCallbackSubmitBody,
): Promise<PerformanceLabCallbackApiSuccess> => {
  const response = await fetch('/api/performance-lab/callbacks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const data = (await response.json()) as
    | PerformanceLabCallbackApiSuccess
    | PerformanceLabCallbackApiError;

  if (!response.ok || data.status === 'error') {
    const message =
      data.status === 'error'
        ? data.error?.message ?? data.message ?? 'Something went wrong.'
        : 'Something went wrong.';
    throw new Error(message);
  }

  return data;
};
